// src-lit/components/product/kd-product-spec.ts
import { LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { onEvent } from '../../utils/dom';
import { EVENT_NAMES, type VariantSelectedDetail } from '../../constants/events';
import { SELECTORS } from '../../constants/selectors';

interface SpecVariantEntry {
  /** snippet v2-product-spec-rows の事前レンダリング済み HTML（<tr> 連続）。Liquid 側で
   *  variant ごとに row 順序を確定してから JSON 化しているため、Lit 側は innerHTML 差替のみ。
   *  variant ごとに行構成が異なる（production_on / rice_miling_timing / expiration の有無）ため、
   *  textContent 単位の差替ではなく tbody 全体を innerHTML 置換する。 */
  rowsHtml: string;
}

/**
 * @element kd-product-spec
 * @description variant 切替に応じて仕様テーブルの行群を更新する。
 *   行の順序 / 表示有無は variant 依存（賞味期限 / 製造日 / 精米時期 metafield 等）。
 *   variant ごとに rows_html を Liquid で事前レンダリングして JSON に埋め込み、
 *   Lit は対応する <tbody> の innerHTML を差し替えるだけ（文字列生成は Liquid に集約）。
 */
@customElement('kd-product-spec')
export class KdProductSpec extends LitElement {
  createRenderRoot() {
    return this;
  }

  private unsubscribe: (() => void) | null = null;

  private variantMap: Record<string, SpecVariantEntry | undefined> = {};

  render() {
    return nothing;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.loadVariantMap();
    this.unsubscribe = onEvent<VariantSelectedDetail>(
      document,
      EVENT_NAMES.PRODUCT.VARIANT_SELECTED,
      (e) => this.handleVariantSelected(e.detail),
    );
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.unsubscribe?.();
    this.unsubscribe = null;
  }

  private loadVariantMap(): void {
    const script = this.querySelector<HTMLScriptElement>(
      SELECTORS.PRODUCT_SPEC.VARIANT_MAP,
    );
    if (!script?.textContent) return;
    try {
      this.variantMap = JSON.parse(script.textContent);
    } catch {
      // JSON parse 失敗時は空マップのまま
    }
  }

  private handleVariantSelected(detail: VariantSelectedDetail): void {
    const entry = this.variantMap[detail.variantId];
    if (!entry) return;
    const tbody = this.querySelector<HTMLElement>(
      SELECTORS.PRODUCT_SPEC.TABLE_BODY,
    );
    if (!tbody) return;
    // rowsHtml は v2-product-spec-rows.liquid → v2-table-row.liquid 経由で出力され、
    // ラベル / 値はすべて `escape_once` 済（metafield に <script> が混入しても無害化される）。
    tbody.innerHTML = entry.rowsHtml;
    // 切替先 variant が行を 1 つも持たないと空 <table> が見えるため、table 自体を hidden する。
    const table = tbody.closest('table');
    if (table) table.hidden = entry.rowsHtml.trim() === '';
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kd-product-spec': KdProductSpec;
  }
}
